
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { usePurchases } from './usePurchases';
import PurchaseCard from './PurchaseCard';
import CancelOrderDialog from './CancelOrderDialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import SearchFilterBar from './SearchFilterBar';
import { Order } from './order-types';
import { isWithinInterval, parseISO } from 'date-fns';

interface UserPurchasesProps {
  userId: string;
}

const UserPurchases = ({ userId }: UserPurchasesProps) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [filteredPurchases, setFilteredPurchases] = useState<Order[]>([]);
  
  const {
    purchases,
    isLoading,
    error,
    cancelDialogOpen,
    setCancelDialogOpen,
    confirmingDelivery,
    confirmingReceipt,
    cancellationReason,
    setCancellationReason,
    handleCancelOrder,
    handleConfirmDelivery,
    handleConfirmReceipt,
    confirmCancelOrder
  } = usePurchases(userId);
  
  // Filter purchases based on search term and date range
  useEffect(() => {
    if (!purchases) {
      setFilteredPurchases([]);
      return;
    }
    
    let filtered = [...purchases];
    
    // Apply text search
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(purchase => 
        purchase.listings?.title?.toLowerCase().includes(lowerCaseSearch)
      );
    }
    
    // Apply date filters
    if (startDate || endDate) {
      filtered = filtered.filter(purchase => {
        const purchaseDate = parseISO(purchase.created_at);
        
        // Both start and end dates specified
        if (startDate && endDate) {
          return isWithinInterval(purchaseDate, { start: startDate, end: endDate });
        } 
        // Only start date specified
        else if (startDate) {
          return purchaseDate >= startDate;
        } 
        // Only end date specified
        else if (endDate) {
          return purchaseDate <= endDate;
        }
        return true;
      });
    }
    
    setFilteredPurchases(filtered);
  }, [purchases, searchTerm, startDate, endDate]);
  
  const handleResetFilters = () => {
    setSearchTerm('');
    setStartDate(undefined);
    setEndDate(undefined);
  };
  
  const handleViewListing = (listingId: number) => {
    navigate(`/product/${listingId}`);
  };

  const handleViewSellerProfile = (sellerId: string) => {
    navigate(`/user-profile?user=${sellerId}`);
  };

  const handleContactSeller = (order: any) => {
    if (!order.seller_profile?.id) return;
    
    navigate(`/messages`);
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <p className="mt-4 text-red-500">Gabim gjatë ngarkimit të blerjeve</p>
      </div>
    );
  }
  
  if (!purchases || purchases.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-500">Nuk keni blerje ende.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <SearchFilterBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        onReset={handleResetFilters}
      />
    
      <ScrollArea className="h-[60vh] md:h-[500px] w-full pr-4" type="always">
        <div className="grid grid-cols-1 gap-4 p-4">
          {filteredPurchases.map((purchase) => (
            <PurchaseCard
              key={purchase.id}
              purchase={purchase}
              confirmingDelivery={confirmingDelivery}
              confirmingReceipt={confirmingReceipt}
              onViewListing={handleViewListing}
              onViewSellerProfile={handleViewSellerProfile}
              onContactSeller={handleContactSeller}
              onCancelOrder={handleCancelOrder}
              onConfirmDelivery={handleConfirmDelivery}
              onConfirmReceipt={handleConfirmReceipt}
            />
          ))}
        </div>

        {filteredPurchases.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">Nuk u gjetën blerje që përputhen me filtrat.</p>
          </div>
        )}
      </ScrollArea>

      {/* Cancel Order Confirmation Dialog */}
      <CancelOrderDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={confirmCancelOrder}
        isPending={!!confirmingDelivery}
        reason={cancellationReason}
        onReasonChange={setCancellationReason}
        title="Anulo Porosinë"
        description="Ju lutemi shpjegoni arsyen e anulimit të porosisë. Shitësi do t'ju kontaktojë."
      />
    </div>
  );
};

export default UserPurchases;
